<script lang="ts">
    import { Drawer, Button } from "flowbite-svelte";
    import {drawerStore, closeDrawer, saveObject} from "../../services/stores/object-drawer-store.js";
    import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
    import { faTimes, faArrowLeft, faArrowRight, faSave } from "@fortawesome/free-solid-svg-icons";
    import UserObject from "../objects/UserObject.svelte";
    import type { SvelteComponent } from 'svelte';
    import CustomerSelection from "../objects/CustomerSelection.svelte";

    interface CustomerSelectDetail {
        customerId: string;
        checked: boolean;
    }

    interface Customer {
        customer_id: string;
        name: string;
        status?: string;
    }

    let activateClickOutside = false;
    let currentStep = 1;
    let hidden = true;
    
    let transitionParams = {
        x: -320,
        duration: 200
    };
    
    $: objectData = $drawerStore.object || {
        user: {
            user_id: "",
            name: "",
            email: "",
            oid: "",
            user_type: "",
            user_role: "",
            claims: {},
            metadata: {},
            customers: []
        },
        lookups: {
            customer: { list: [] },
            user_type: { list: [] }
        }
    };

    $: userObjectData = (currentStep === 1 && tempUserData) ? {
        user: tempUserData,
        lookups: objectData?.lookups
    } : objectData;
    let tempUserData: any = null;

    let selectedCustomers: (Customer | string)[] = [];

    let userObjectComponent: SvelteComponent;

    let isNextButtonDisabled = true;
    let isPreviousButtonDisabled = true;
    let isSaveButtonDisabled = true;

    // Button state management
    $: {
        // Previous button is enabled only on step 2
        isPreviousButtonDisabled = currentStep === 1;

        // Next button is enabled on step 1 when required fields are filled
        if (currentStep === 1) {
            isNextButtonDisabled = !userObjectData?.user?.oid || 
                                 !userObjectData?.user?.user_type || 
                                 !userObjectData?.user?.user_role;
        }

        // Save button is enabled on step 2 when:
        // 1. At least one customer is selected OR
        // 2. User is an OH admin user
        if (currentStep === 2) {
            const isOHUserAdmin = tempUserData?.user_type === "oh-user" && tempUserData?.user_role === "admin";
            isSaveButtonDisabled = !isOHUserAdmin && selectedCustomers.length === 0;
        } else {
            isSaveButtonDisabled = true;
        }
    }

    let title = $drawerStore.action === 'add' ? 'Add User' : 'Edit User';
    $: if ($drawerStore.open) {
        title = $drawerStore.action === 'add' ? 'Add User' : 'Edit User';
        hidden = false;
    }

    $: if ($drawerStore.open !== !hidden) {
        hidden = !$drawerStore.open;
    }

    // Initialize selectedCustomers when drawer opens with edit data
    $: if ($drawerStore.open && $drawerStore.objectType === "user") {
        currentStep = 1;
        tempUserData = null;
        // Convert customer IDs to full customer objects
        selectedCustomers = $drawerStore.object?.user?.customers?.map((customer: Customer | string) => 
            typeof customer === 'string' ? 
                objectData?.lookups?.customer?.list?.find((c: Customer) => c.customer_id === customer) : 
                customer
        ).filter(Boolean) || [];
    }

    function handleNext(_event?: MouseEvent) {
        // Save the current state of UserObject
        tempUserData = {
            ...userObjectData.user,
            customers: userObjectData.user.customers || selectedCustomers || []
        };
        selectedCustomers = tempUserData.customers;
        currentStep = 2;
    }
    
    function handlePrevious() {
        currentStep = 1;
    }

    function handleSave() {
        const selectedUserType = objectData?.lookups?.user_type?.list?.find((t: any) => t.id === tempUserData.user_type);
        const selectedRole = selectedUserType?.role?.list?.find((r: any) => r.id === tempUserData.user_role);
        const claims = selectedRole?.claims || {};

        const userData = {
            ...tempUserData,
            customers: selectedCustomers.map((customer: Customer | string) => 
                typeof customer === 'string' ? customer : customer.customer_id
            ),
            user_id: tempUserData.user_id,
            claims: claims,
            metadata: {}
        };
        saveObject(userData);
    }

    function handleClose() {
        currentStep = 1;
        tempUserData = null;
        selectedCustomers = [];
        closeDrawer();
    }

    $: if (!$drawerStore.open) {
        currentStep = 1;
        tempUserData = null;
        selectedCustomers = [];
    }

    function handleCustomerSelect(customerId: string, checked: boolean) {
        const isCustomerUser = tempUserData?.user_type === "customer";
        const customerData = objectData?.lookups?.customer?.list?.find((c: Customer) => c.customer_id === customerId);
        if (!customerData) return;
        
        if (isCustomerUser) {
            // For customer users, ensure we only have one selection
            selectedCustomers = checked ? [customerData] : [];
        } else {
            // Multi-select behavior for other user types
            if (checked) {
                selectedCustomers = [...selectedCustomers, customerData];
            } else {
                selectedCustomers = selectedCustomers.filter(customer => 
                    typeof customer === 'string' 
                        ? customer !== customerId
                        : customer.customer_id !== customerId
                );
            }
        }
    }

    $: if (currentStep === 1 && tempUserData) {
        setTimeout(() => {
            if (userObjectComponent) {
                userObjectComponent.$set({ object: userObjectData });
            }
        }, 0);
    }

    function isStep1Valid(userData: any) {
        const validationState = {
            oid: Boolean(userData?.oid?.trim()),
            name: Boolean(userData?.name?.trim()),
            email: Boolean(userData?.email?.trim()),
            user_type: Boolean(userData?.user_type?.trim()),
            user_role: Boolean(userData?.user_role?.trim())
        };
        
        return validationState.oid && 
               validationState.name && 
               validationState.email && 
               validationState.user_type && 
               validationState.user_role;
    }

    $: {
        const valid = isStep1Valid(userObjectData?.user);
        isNextButtonDisabled = !userObjectComponent || 
            !userObjectData?.user || 
            !valid;
    }

    $: if (currentStep === 2) {
    }

    function handleFieldChange(event: CustomEvent) {
        const updatedUser = event.detail;
        userObjectData = {
            ...userObjectData,
            user: {
                ...userObjectData.user,
                ...updatedUser
            }
        };
    }
</script>

<div class="drawer-container">
    <Drawer 
        id="user-drawer" 
        class="object-drawer" 
        {activateClickOutside} 
        transitionType="fly" 
        {transitionParams} 
        placement="left" 
        backdrop={true} 
        bind:hidden
    >
        <div class="drawer-header">
            <div class="drawer-title-container">
                <h2>{title}</h2> (STEP {currentStep} of 2)
            </div>
            <Button class="drawer-header-button" on:click={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
            </Button>
        </div>

        {#if !hidden}
            <div class="flex flex-col">
                {#if currentStep === 1}
                    <div class="flex flex-col" style="height:calc(100vh - 13rem);">
                        <UserObject
                            bind:this={userObjectComponent}
                            object={userObjectData}
                            action={$drawerStore.action || "add"}
                            on:save={handleSave}
                            on:close={handleClose}
                            on:fieldChange={(e) => {
                                handleFieldChange(e);
                                const valid = isStep1Valid(e.detail);
                                isNextButtonDisabled = !userObjectComponent || !valid;
                            }}
                        />
                    </div>
                {:else if currentStep === 2}
                    <div class="flex flex-col" style="height:calc(100vh - 13rem);">
                        <CustomerSelection 
                            userData={tempUserData}
                            lookups={objectData?.lookups}
                            on:customerSelect={(event) => handleCustomerSelect(event.detail.customerId, event.detail.checked)}
                        />
                    </div>
                {/if}

                <div class="p-2 flex justify-between drawer-footer">
                    <div class="flex justify-start gap-2">
                        <Button class="next-button" size="xs" color="light" disabled={isPreviousButtonDisabled} on:click={handlePrevious}>
                            <FontAwesomeIcon icon={faArrowLeft} class="mr-2" />Previous
                        </Button>
                        <Button class="previous-button" size="xs" color="light" disabled={isNextButtonDisabled} on:click={handleNext}>
                            Next<FontAwesomeIcon icon={faArrowRight} class="ml-2" />
                        </Button>
                    </div>
                    <div class="flex justify-end gap-2">
                        <Button class="save-button" size="xs" color="primary" disabled={isSaveButtonDisabled} on:click={handleSave}>
                            <FontAwesomeIcon icon={faSave} class="mr-2" />Save
                        </Button>
                        <Button class="cancel-button" size="xs" color="light" on:click={handleClose}>
                            <FontAwesomeIcon icon={faTimes} class="mr-2" />Cancel
                        </Button>
                    </div>
                </div>
            </div>
        {/if}
    </Drawer>
</div>

